import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { CircularProgress, Grid, Hidden } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { Text } from '@pbt/pbt-ui-components'

import { ContextualBackButton } from '~/components/common/buttons/ContextualBackButton'
import { RightRailOverlayProvider } from '~/components/dashboard/soapV2/context/RightRailOverlayContext'
import { PatientMedicalSummaryRail } from '~/components/dashboard/soapV2/summary/PatientMedicalSummaryRail'
import { fetchClient } from '~/store/actions/clients'
import {
  fetchPrescription,
  getPrescriptionById,
  getPrescriptionIsLoading,
} from '~/store/duck/prescriptions'

import ClientAndPatientSection from './details/new-client-and-patient/ClientAndPatientSection'

const useStyles = makeStyles(
  (theme) => ({
    root: {
      height: '100%',
      flexWrap: 'nowrap',
    },
    content: {
      flex: 1,
      padding: theme.spacing(3),
      overflow: 'auto',
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
    },
    header: {
      marginBottom: theme.spacing(3),
    },
    rightRail: {
      width: 'max(420px, 35vw)',
      height: '100%',
      borderLeft: `1px solid ${theme.colors.lowAccentText}`,
    },
  }),
  { name: 'PrescriptionDetailsPage' },
)

const PrescriptionDetailsPage = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation('Common')
  const { prescriptionId } = useParams<{ prescriptionId: string }>()

  const prescription = useSelector(getPrescriptionById(prescriptionId))
  const isLoading = useSelector(getPrescriptionIsLoading)

  useEffect(() => {
    if (prescriptionId) {
      dispatch(fetchPrescription(prescriptionId))
    }
  }, [prescriptionId, dispatch])

  useEffect(() => {
    if (prescription?.client?.id) {
      dispatch(
        fetchClient({
          clientId: prescription.client.id,
          patientId: prescription?.patient?.id,
        }),
      )
    }
  }, [prescription?.client?.id, prescription?.patient?.id, dispatch])

  if (isLoading || !prescription) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    )
  }

  const clientId = prescription?.client?.id
  const patientId = prescription?.patient?.id

  const handleBackNavigation = () => {
    if (clientId && patientId) {
      navigate(`/client/${clientId}/patient/${patientId}`)
    } else if (clientId) {
      navigate(`/client/${clientId}`)
    } else {
      navigate(-1)
    }
  }

  return (
    <Grid container className={classes.root}>
      <Hidden lgDown>
        <Grid item>
          <ClientAndPatientSection
            clientId={clientId || ''}
            patientId={patientId}
          />
        </Grid>
      </Hidden>
      <Grid container item xs direction="column" wrap="nowrap">
        <ContextualBackButton
          buttonText={t('Common:CLIENT_AND_PATIENT_DETAIL')}
          navigateBackToContext={handleBackNavigation}
        />
        <div className={classes.content}>
          <div className={classes.header}>
            {/* TODO CVC-30339: Add prescription details content here */}
            <Text variant="h1">{prescription.name || ''}</Text>
          </div>
        </div>
      </Grid>
      <Hidden mdDown>
        <Grid item className={classes.rightRail}>
          <RightRailOverlayProvider>
            <PatientMedicalSummaryRail
              clientId={clientId || ''}
              patientId={patientId || ''}
            />
          </RightRailOverlayProvider>
        </Grid>
      </Hidden>
    </Grid>
  )
}

export { PrescriptionDetailsPage }
